import { createElement, lazy, type ComponentProps, type ComponentType } from 'react'

// `any` is the canonical React idiom for "any component"; `unknown` breaks the ComponentType constraint.
// oxlint-disable-next-line typescript-eslint/no-explicit-any
type Loader<T extends ComponentType<any>> = () => Promise<{ default: T }>

// oxlint-disable-next-line typescript-eslint/no-explicit-any
export type PreloadableComponent<T extends ComponentType<any>> = ComponentType<ComponentProps<T>> & {
  preload: () => Promise<{ default: T }>
}

// Like React.lazy plus .preload(), rendering synchronously once resolved, so later mounts skip the fallback.
// createElement, not JSX: the React Compiler emits the binding name as a string host type for this mutable slot.
// oxlint-disable-next-line typescript-eslint/no-explicit-any
export function lazyPreloadable<T extends ComponentType<any>>(loader: Loader<T>): PreloadableComponent<T> {
  let resolved: T | null = null
  let cached: Promise<{ default: T }> | null = null

  const sharedLoader = () => {
    if (cached) return cached
    cached = loader().then((mod) => {
      resolved = mod.default
      return mod
    })
    return cached
  }

  // The same shared loader, so a .preload() call and a suspended render share one Promise.
  const Lazy = lazy(sharedLoader)

  function Preloadable(props: ComponentProps<T>) {
    if (resolved) return createElement(resolved, props)
    return createElement(Lazy, props)
  }

  ;(Preloadable as PreloadableComponent<T>).preload = sharedLoader
  return Preloadable as PreloadableComponent<T>
}
